import {useState, useEffect} from 'react'
import {useAppSelector, useAppDispatch} from '@/store'
import {addExcludedProduct, addIncludedProduct, resetRuleSet} from '@/store/slices/salesTargetSlice'

// Organisms
import {CategoryWrapper, PageContainer, ProductsTable} from '@/components/organisms'
import {FileUploadWithoutModal} from '@/components/organisms/FileUploadWithoutModal'

// Molecules
import {PageHeader} from '@/components/molecules'

// Atoms
import {RadioButton, Tag} from '@/components/atoms'

// Constants
import {Constants, NUMBERS} from '@/utils/constants/commonConstants'
import {TAG_COLOR} from '@/utils/constants/colors'

// Utils
import {uploadFileDescriptionForCSV} from '@/utils/commonTemplate'

// Helpers
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper'
import {downloadTemplateFile} from '@/services/downloadTemplateFile'
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils'

// Define the base cell data mapper object without the "includeExclude" key as it references functions defined later.
const cellDataMapperObj = {
  productName: ({row: {original} = {}}) => {
    return <div>{original?.productName}</div>
  },
  productId: ({row: {original} = {}}) => {
    return <div>{original?.productId}</div>
  },
  displayName: ({row: {original} = {}}) => {
    return <div>{original?.displayName?.en}</div>
  },
  displaySize: ({row: {original} = {}}) => {
    return <div>{original?.displaySize?.en}</div>
  },
  displayCategory: ({row: {original} = {}}) => {
    return <div>{original?.displayCategory?.en}</div>
  },
  brand: ({row: {original} = {}}) => {
    return <div>{original?.brand?.en}</div>
  },
  subcategory: ({row: {original} = {}}) => {
    return <div>{original?.subcategory?.en}</div>
  },
}

// eslint-disable-next-line react-refresh/only-export-components
export const ProductListColConfig = [
  {
    name_of_db_column: 'productName',
    label_of_column: 'Name',
    order_of_column: 1,
  },
  {
    name_of_db_column: 'productId',
    label_of_column: 'Product ID',
    order_of_column: 2,
  },
  {
    name_of_db_column: 'status',
    label_of_column: 'Status',
    order_of_column: 2,
  },
  {
    name_of_db_column: 'displaySize',
    label_of_column: 'Size',
    order_of_column: 3,
  },
  {
    name_of_db_column: 'displayCategory',
    label_of_column: 'Category',
    order_of_column: 4,
  },
  {
    name_of_db_column: 'brand',
    label_of_column: 'Brand',
    order_of_column: 5,
  },
  {
    name_of_db_column: 'includeExclude',
    label_of_column: 'Include/Exclude',
    order_of_column: 6,
  },
]

const SalesTargetProducts = props => {
  const {
    setSelectedType,
    selectedType,
    uploadComplete,
    onUpdate,
    formData = {},
    setFormData,
    uploadError,
    setUploadError,
    selectedCategory,
    setSelectedCategory,
    ProductListColConfig,
    setRowSelection,
    rowSelection,
    handleBulkAction,
    categoryListData,
    productListData,
    targetType,
  } = props

  const dispatch = useAppDispatch()

  // Store included/excluded product state here.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [productIncludeMap, setProductIncludeMap] = useState({})

  // Log the product include/exclude map when it changes
  // useEffect(() => {
  //   console.log('Product include/exclude map updated:', productIncludeMap)
  // }, [productIncludeMap])

  // "localProductData" holds the table rows with an "included" key.
  const [localProductData, setLocalProductData] = useState([])

  // Pull in includedProducts (or excludedProducts) from Redux
  const includedProducts = useAppSelector(state => state.salesTarget.includedProducts)
  useEffect(() => {
    if (productListData?.data?.length) {
      // Rebuild local data to reflect updated includes from Redux
      const dataWithIncluded = productListData.data.map(product => ({
        ...product,
        included: includedProducts?.includes(product.productId),
      }))
      setLocalProductData(dataWithIncluded)
    }
  }, [
    productListData,
    includedProducts, // <-- add these dependencies
  ])

  /**
   * Toggle inclusion for both local state (for UI) and Redux state (for final form).
   */
  const handleRowIncludeExcludeLocal = (productId, shouldInclude) => {
    // First, update Redux. This ensures our global store is correct.
    if (shouldInclude) {
      dispatch(addIncludedProduct(productId))
    } else {
      dispatch(addExcludedProduct(productId))
    }
    // Then update local/UI state
    setLocalProductData(prevData =>
      prevData.map(p => (p.productId === productId ? {...p, included: shouldInclude} : p))
    )
  }

  // This cell mapper uses the "included" key from local data.
  const localCellDataMapperObjLocal = {
    ...cellDataMapperObj,
    includeExclude: ({row: {original} = {}}) => {
      const included = original?.included
      return (
        <Tag
          text={included ? 'Exclude' : 'Include'}
          color={included ? TAG_COLOR.RED_150 : TAG_COLOR.GREEN_150}
          onClick={() => handleRowIncludeExcludeLocal(original?.productId, !included)}
        />
      )
    },
  }

  const cellDataMappedColsLocal = getCellDataMappedCols(localCellDataMapperObjLocal, ProductListColConfig)

  const handleDelete = () => {
    setFormData()
    dispatch(resetRuleSet())
  }

  const handleFileDownload = async () => {
    const body = {type: 'Product Template'}
    const response = await downloadTemplateFile(body)
    if (!response) return
    const fileName = `SalesTargetProductTemplate_${getDateWithTimeString()}`
    downloadCSVFiles(response, fileName)
  }

  return (
    <>
      <PageContainer>
        <div className="sales-target-component gray850 text-white">
          <div className="flex items-center justify-between">
            <PageHeader title={'Products'} />
          </div>
          <div className="radio-wrap flex">
            <RadioButton
              isDark={true}
              // items={['Category & Products', 'Upload File with Products']}
              items={['Upload File with Products']}
              onUpdate={category => {
                setSelectedType(category === NUMBERS.Zero ? Constants.CategoryProducts : Constants.UploadedProducts)
              }}
              reference="radio button"
              selected={selectedType === Constants.CategoryProducts || selectedType === Constants.UploadedProducts}
              size="small"
              className="mr-8"
              id={'uploadedProducts'}
            />
          </div>
          <hr className="products-hr" />
          {selectedType === Constants.CategoryProducts && (
            <CategoryWrapper
              categories={categoryListData}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          )}
          {selectedType === Constants.UploadedProducts && (
            <FileUploadWithoutModal
              handleDownloadTemplate={handleFileDownload}
              handleViewTable={() => {}}
              description={uploadFileDescriptionForCSV()}
              headerTitle={Constants.UploadProducts}
              acceptedExtensions={Constants.CSV}
              files={formData?.mediaFile?.length > 0 ? [formData?.mediaFile] : []}
              onUpload={file => {
                setFormData(prev => ({
                  ...prev,
                  ...{mediaFile: [file?.name]},
                }))
                setUploadError(false)
              }}
              feedbackType={uploadError ? Constants.Error : Constants.Info}
              helperText={uploadError ? 'File format is not valid. Supported formats: .csv' : ''}
              onUploadError={error => {
                if (error) {
                  setUploadError(true)
                }
              }}
              onUploadDelete={handleDelete}
              variant={formData?.mediaFile?.length > 0 ? Constants.Complete : Constants.Edit}
              type={Constants.Single}
              className="!max-w-full"
              containerClassName="!w-full"
              isCsvValidationRequired={true}
              required={targetType == 'Main Target' ? false : true}
            />
          )}
        </div>
      </PageContainer>
      {selectedType === Constants.CategoryProducts && (
        <ProductsTable
          onUpdate={onUpdate}
          isUploadTable={false}
          columns={cellDataMappedColsLocal}
          records={localProductData}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          handleBulkAction={handleBulkAction}
        />
      )}
      {uploadComplete && (
        <ProductsTable isUploadTable={true} rowSelection={rowSelection} setRowSelection={setRowSelection} />
      )}
    </>
  )
}

export default SalesTargetProducts
